import * as React from 'react';
import styled from 'styled-components';
import { typography, spacing, shape } from '@govuk-react/lib';
import { stripUnit } from 'polished';
import { LINK_COLOUR, LINK_HOVER_COLOUR, FOCUS_COLOUR, BLACK, BORDER_COLOUR } from 'govuk-colours';
import { FOCUS_WIDTH, SPACING_POINTS, BORDER_WIDTH } from '@govuk-react/constants';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

var _excluded = ["summary", "children"];
var CUSTOM_FOCUS_WIDTH = "".concat(Number(stripUnit(FOCUS_WIDTH)) + 1, "px");
var StyledDetails = styled('details').withConfig({
  displayName: "src__StyledDetails",
  componentId: "sc-273hbe-0"
})(typography.font({
  size: 19
}), typography.textColour, spacing.withWhiteSpace({
  marginBottom: 6
}), {
  display: 'block'
});
var StyledSummary = styled('summary').withConfig({
  displayName: "src__StyledSummary",
  componentId: "sc-273hbe-1"
})({
  display: 'inline-block',
  position: 'relative',
  marginBottom: SPACING_POINTS[1],
  paddingLeft: Number(stripUnit(SPACING_POINTS[4])) + Number(stripUnit(BORDER_WIDTH)),
  color: LINK_COLOUR,
  cursor: 'pointer',
  ':hover': {
    color: LINK_HOVER_COLOUR
  },
  ':focus': {
    outline: "".concat(CUSTOM_FOCUS_WIDTH, " solid ").concat(FOCUS_COLOUR),
    outlineOffset: -1,
    color: BLACK,
    background: FOCUS_COLOUR
  },
  '::-webkit-details-marker': {
    display: 'none'
  },
  ':before': _objectSpread2(_objectSpread2({
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    margin: 'auto'
  }, shape.arrow({
    direction: 'right',
    base: 14
  })), {}, {
    '[open] > &': shape.arrow({
      direction: 'down',
      base: 14
    })
  })
});
var SummaryText = styled('span').withConfig({
  displayName: "src__SummaryText",
  componentId: "sc-273hbe-2"
})({
  textDecoration: 'underline'
});
var DetailsText = styled('div').withConfig({
  displayName: "src__DetailsText",
  componentId: "sc-273hbe-3"
})({
  padding: SPACING_POINTS[3],
  paddingLeft: SPACING_POINTS[4],
  borderLeft: "".concat(BORDER_WIDTH, " solid ").concat(BORDER_COLOUR),
  p: {
    marginTop: 0,
    marginBottom: SPACING_POINTS[4]
  },
  '> :last-child, p:last-child': {
    marginBottom: 0
  }
});
/**
 * Make a page easier to scan by letting users reveal more detailed information only if they need it.
 *
 * - https://govuk-react.github.io/govuk-react/?path=/docs/details
 * - https://design-system.service.gov.uk/components/details/
 */

var Details = function Details(_ref) {
  var summary = _ref.summary,
      children = _ref.children,
      props = _objectWithoutProperties(_ref, _excluded);

  return /*#__PURE__*/React.createElement(StyledDetails, props, /*#__PURE__*/React.createElement(StyledSummary, null, /*#__PURE__*/React.createElement(SummaryText, null, summary)), /*#__PURE__*/React.createElement(DetailsText, null, children));
};
Details.defaultProps = {
  children: undefined,
  open: false
};
Details.displayName = 'Details';

export { Details, Details as default };
